// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Toaster from "@insite/mobius/Toast/Toaster";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { get } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionContainer, SectionTitle } from "@tps/App/Widgets/DesignSystem/DesignSystem.helpers";
import { clipboardText, getAllColorsAsDropdownOptions } from "@tps/App/Widgets/DesignSystem/Utilities";
import systemColors from "@tps/XDL/PreStyle/colors";
import React, { FC } from "react";
import { css } from "styled-components";

const ColorItem = ({ name, styles }: { name: string; styles: ColorItemStyles }) => {
    const toaster = useToaster();
    return (
        <Clickable
            onClick={() => {
                const isCopied = clipboardText(name);
                toaster.addToast({
                    body: isCopied ? (
                        <Typography>
                            <strong>{name}</strong> color is copied
                        </Typography>
                    ) : (
                        <Typography>
                            copy <strong>{name}</strong> color failed
                        </Typography>
                    ),
                    messageType: isCopied ? "success" : "danger",
                });
            }}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper
                    css={css`
                        width: 100%;
                        height: 82px;
                        background: ${getColor(name)};
                    `}
                />
                <Typography {...styles.title}>{name}</Typography>
                <Typography {...styles.subTitle}>{get(systemColors, name)}</Typography>
            </StyledWrapper>
        </Clickable>
    );
};

interface ColorItemStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
}

interface ColorsTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    pathTypography?: TypographyPresentationProps;
    colorsContainer?: InjectableCss;
    colorItem: ColorItemStyles;
}

const colorsTabStyles: ColorsTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
        `,
    },
    pathTypography: {
        size: 15,
        css: css`
            margin-left: 0;
            margin-bottom: 15px;
            background: #f4f4f4;
            color: #666;
            line-height: 1.6;
            max-width: 100%;
            overflow: auto;
            padding: 15px 22px;
            display: block;
            word-wrap: break-word;
            user-select: none;
            border: 1px solid #ddd;
            border-left: 3px solid #298CF0;
        `,
    },
    colorsContainer: {
        css: css`
            display: flex;
            gap: 48px;
            flex-wrap: wrap;
            justify-content: flex-start;
            padding: 15px 0;
        `,
    },
    colorItem: {
        container: {
            css: css`
                overflow-wrap: break-word;
                width: 170px;
                display: flex;
                flex-direction: column;

                border-radius: 8px;
                box-shadow: rgb(0 58 99 / 15%) 0 0 9px 0;
                transition: box-shadow ease-in 150ms;

                & > div {
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    border-bottom: 1px solid #cccccc96;
                }
                &:hover {
                    box-shadow: rgb(0 58 99 / 30%) 0 0 15px 0;
                }
                &:focus {
                    outline: none;
                }
            `,
        },
        title: {
            size: 14,
            css: css`
                color: #212121;
                display: block;
                margin-top: 15px;
                padding: 0 8px;
            `,
        },
        subTitle: {
            size: 12,
            css: css`
                color: #5c6f7c;
                display: block;
                padding: 0 8px;
                margin-top: 8px;
                margin-bottom: 10px;
            `,
        },
    },
};

const styles = colorsTabStyles;
const ColorsTab: FC = prop => {
    return (
        <SectionContainer>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Colors")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Toaster position="bottom" mobilePosition="bottom">
                    <Typography {...styles.pathTypography}>
                        {translate("Click on the color item to copy color path")}
                    </Typography>
                    <StyledWrapper {...styles.colorsContainer}>
                        {getAllColorsAsDropdownOptions(systemColors).map((colorItem, index) => (
                            <ColorItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={`color_${colorItem.value}_${index}`}
                                name={colorItem.displayName ?? ""}
                                styles={styles.colorItem}
                            />
                        ))}
                    </StyledWrapper>
                </Toaster>
            </GridItem>
        </SectionContainer>
    );
};

export default ColorsTab;
